import { z } from 'zod';

import { emailSchema, usernameSchema } from './user';
import { PEOPLE_SORTS, activityFilterSchema, listQuerySchema, sortParam } from './pagination';
import type { ShiftView } from './employee-portal';

/*
 * Employees. reference/data-model.md §3, ROADMAP.md §3.4.
 *
 * The identity half — email, username, recovery email — is not restated here.
 * It comes from ./user.ts, because an employee's account has to obey exactly the
 * same rules as any other (P9: an account with no email needs a recovery
 * address, or it can never be reset and never be contacted). A second copy of
 * that rule is a second place for it to be wrong.
 */

const trimmed = (max: number, label = 'Required') =>
  z
    .string()
    .trim()
    .min(1, label)
    .max(max, `Must be ${String(max)} characters or fewer`);

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${String(max)} characters or fewer`)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional();

/** A calendar day, `YYYY-MM-DD`. Never a datetime — CLAUDE.md §4. */
export const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-04')
  .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'That is not a real date');

const employeeFields = z.object({
  firstName: trimmed(128),
  lastName: trimmed(128),
  phone: optional(32),

  address: optional(255),
  city: optional(128),
  state: optional(64),
  zip: optional(16),

  /**
   * How many jobs a day this person will take.
   *
   * The prototype has it, the Airo roadmap dropped it, and auto-assignment must
   * respect it (business-rules.md §4). Default 3.
   */
  maxJobsPerDay: z
    .number()
    .int('Give a whole number of jobs')
    .min(1, 'At least one job a day')
    .max(20, 'More than twenty jobs a day is not a real number'),

  /**
   * Integer cents. **Admin-only** — never returned to an employee about anyone
   * but themselves, which `employee-service.ts` enforces rather than this
   * schema.
   */
  hourlyRateCents: z
    .number()
    .int('Money is whole cents')
    .min(0, 'A rate cannot be negative')
    .max(100_000_000)
    .nullable()
    .optional(),

  positionId: z.uuid().nullable().optional(),
  hireDate: calendarDateSchema.nullable().optional(),

  /** Skill ids. The whole set each time, so removing one is the same call. */
  skillIds: z.array(z.uuid()).max(100).optional(),
});

/**
 * Creating an employee creates their login too.
 *
 * There is no public sign-up, so this is the only route into the business for a
 * cleaner. The two identity rules come straight from ./user.ts: an account needs
 * something to sign in with, and if that is a username rather than an email it
 * needs a recovery address — which is exactly the shape the prototype broke
 * (P9), and exactly the shape Honeycomb needs, because cleaners are hired
 * without a work email.
 */
export const createEmployeeSchema = employeeFields
  .extend({
    email: emailSchema.optional(),
    username: usernameSchema.optional(),
    recoveryEmail: emailSchema.optional(),
  })
  .refine((value) => Boolean(value.email ?? value.username), {
    message: 'Give them an email address or a username to sign in with',
    path: ['email'],
  })
  .refine((value) => Boolean(value.email ?? value.recoveryEmail), {
    message: 'Add a recovery email — without one this account can never be reset or contacted',
    path: ['recoveryEmail'],
  });

export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;

/**
 * Editing does not touch the login.
 *
 * The prototype marks email and username "Cannot change" and that is good
 * posture — a login identity that moves is one that audit rows and password
 * resets stop agreeing about. Changing it is a deliberate, separate act, not
 * something that happens while somebody corrects a phone number.
 */
export const updateEmployeeSchema = employeeFields;
export type UpdateEmployeeInput = z.infer<typeof updateEmployeeSchema>;

export const employeeListQuerySchema = listQuerySchema.extend({
  sort: sortParam(PEOPLE_SORTS, 'name'),

  activity: activityFilterSchema,
  positionId: z.uuid().optional(),
  skillId: z.uuid().optional(),
});

export interface EmployeeSkillView {
  id: string;
  name: string;
}

/**
 * What an employee looks like to anyone.
 *
 * **No pay rate and no home address.** Both are admin-only. This is the shape
 * phase 6 hands to the employee portal, and the one a client would ever see a
 * subset of.
 */
export interface EmployeeView {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  /** "Sam Cleaner". Built once on the server so two screens cannot disagree. */
  fullName: string;
  phone: string | null;
  maxJobsPerDay: number;
  positionId: string | null;
  /** The display value. Never a slug — P5. */
  positionName: string | null;
  hireDate: string | null;
  isActive: boolean;
  skills: EmployeeSkillView[];
  /** Their login. Either an email or a username; a username account has no email. */
  email: string | null;
  username: string | null;
}

/**
 * What an admin sees, and what an employee sees of **their own** record.
 *
 * business-rules.md §2: "Employee pay rate — admin ✅, employee own only,
 * client ❌". Two types rather than one optional field, so a handler cannot
 * return the rate by forgetting to strip it.
 */
export interface EmployeeAdminView extends EmployeeView {
  hourlyRateCents: number | null;
  recoveryEmail: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  lastLoginAt: string | null;
}

/**
 * One cleaner's record with the numbers that describe their work — the profile
 * page at `/admin/employees/:id`, added 2026-08-06.
 *
 * **The gap this closes is that there was no per-person page at all.** Answering
 * "how has Nina been doing" meant the jobs list filtered by her, then the
 * timesheets screen filtered by her again, and holding the two in your head.
 *
 * **Every figure here is lifetime, not a window**, and the labels say so. A
 * "total jobs" that silently meant the last thirty days would be the kind of
 * number somebody quotes in a review.
 */
export interface EmployeeWorkStats {
  /** Every job they have ever been on the crew of. */
  jobsTotal: number;
  jobsCompleted: number;
  /**
   * Closed shifts only. An open shift has no end and therefore no duration, and
   * counting the minutes so far would make a total that changes between two
   * refreshes — `summarise` in time-service.ts makes the same call.
   */
  minutesClocked: number;
  shifts: number;
}

export interface EmployeeProfile {
  employee: EmployeeAdminView;
  stats: EmployeeWorkStats;
  /**
   * Set when they are on the clock right now. The prototype leads its profile
   * with this and it is the right instinct — "where is everyone" is the question
   * the office asks most.
   */
  openShiftSince: string | null;
  /** The last thirty days, newest first. Payroll lives on /admin/timesheets. */
  recentShifts: ShiftView[];
}

/**
 * The one-time password for a newly created account.
 *
 * Generated on the server and returned exactly once, never stored in readable
 * form — the same deal as `npm run user:create`. The admin creating the account
 * has to pass it on and the person changes it.
 */
export interface CreatedEmployee {
  employee: EmployeeAdminView;
  temporaryPassword: string;
}
